import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import ConsignorDashboard from '../ConsignorDashboard';
import ConsignorContractView from '../ConsignorContractView';
import ConsignorCOView from '../ConsignorCOView';
import ConsignorSettlementView from '../ConsignorSettlementView';

/**
 * ADMIN consignor directory + read-only PREVIEW entry point (AdminRoute-gated).
 *
 * Staff can LIST consignors/consignmentContracts because those rules OR in
 * isPaymentStaff() — a request-based (not doc-based) predicate — so Firestore can
 * authorize the whole collection scan for an admin/auctioneer. Each row opens a
 * read-only preview that renders the REAL consignor pages parameterized by the
 * chosen consignorId (override honored only for staff, inside useConsignorId).
 */

interface Row {
  cid: string;
  name: string;
  email: string;
  legacyCode: string;
  commissionRate: number | null; // percent, from the consignor's contract
  lotCount: number;
}

const AdminConsignors: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [consignorSnap, contractSnap, auctionSnap] = await Promise.all([
          getDocs(collection(db, 'consignors')),
          getDocs(collection(db, 'consignmentContracts')),
          getDocs(collection(db, 'auctions')),
        ]);
        if (!active) return;

        // consignorId -> commissionRate (first contract found for that consignor).
        const rateByCid = new Map<string, number>();
        contractSnap.forEach((d) => {
          const c: any = d.data();
          if (c.consignorId && !rateByCid.has(c.consignorId)) {
            const rate = c?.pricing?.commissionRate;
            if (typeof rate === 'number') rateByCid.set(c.consignorId, rate);
          }
        });

        // consignorId -> #lots across ALL auctions (lots now carry consignorId).
        const lotsByCid = new Map<string, number>();
        auctionSnap.forEach((d) => {
          const lots: any[] = (d.data() as any).lots || [];
          lots.forEach((l) => {
            if (l.consignorId) lotsByCid.set(l.consignorId, (lotsByCid.get(l.consignorId) || 0) + 1);
          });
        });

        const out: Row[] = consignorSnap.docs.map((d) => {
          const c: any = d.data();
          return {
            cid: d.id,
            name: c.name || '(unnamed)',
            email: c.email || '—',
            legacyCode: c.legacyCode || c.hibidCode || '—',
            commissionRate: rateByCid.has(d.id) ? (rateByCid.get(d.id) as number) : null,
            lotCount: lotsByCid.get(d.id) || 0,
          };
        });
        out.sort((a, b) => a.name.localeCompare(b.name));
        setRows(out);
      } catch (err) {
        console.error('Error loading consignors:', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading consignors…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Consignors ({rows.length})</h1>
        <p className="text-gray-600">Open any consignor to preview their portal read-only.</p>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-gray-50">
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3">Email</th>
              <th className="py-2 px-3">Legacy code</th>
              <th className="py-2 px-3 text-right">Commission</th>
              <th className="py-2 px-3 text-right">Lots</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.cid} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 px-3 font-semibold text-gray-900">{r.name}</td>
                <td className="py-2 px-3 text-gray-700">{r.email}</td>
                <td className="py-2 px-3 font-mono text-xs text-gray-500">{r.legacyCode}</td>
                <td className="py-2 px-3 text-right">{r.commissionRate != null ? `${r.commissionRate}%` : '—'}</td>
                <td className="py-2 px-3 text-right">{r.lotCount}</td>
                <td className="py-2 px-3 text-right">
                  <Link
                    to={`/admin/consignors/${r.cid}/dashboard`}
                    className="text-blue-600 hover:text-blue-900 font-semibold"
                  >
                    Preview portal →
                  </Link>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-gray-500">No consignor records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminConsignors;

// ── Read-only preview wrappers ──────────────────────────────────────────────
// Each renders a REAL consignor page with the chosen consignorId as an override and
// a basePath so the page's internal links stay inside the admin preview subtree.

const PreviewFrame: React.FC<{ cid: string; children: React.ReactNode }> = ({ cid, children }) => {
  const [name, setName] = useState('');
  useEffect(() => {
    let active = true;
    getDoc(doc(db, 'consignors', cid))
      .then((s) => { if (active && s.exists()) setName((s.data() as any).name || ''); })
      .catch(() => {});
    return () => { active = false; };
  }, [cid]);
  return (
    <div>
      <div className="bg-amber-100 border-b-2 border-amber-400 text-amber-900 px-4 py-2 text-sm font-semibold text-center">
        👁️ Admin preview — viewing {name ? `as ${name}` : `consignor ${cid}`} (read-only).{' '}
        <Link to="/admin/consignors" className="underline">Back to consignors</Link>
      </div>
      {children}
    </div>
  );
};

export const ConsignorPreviewDashboard: React.FC = () => {
  const { consignorId } = useParams<{ consignorId: string }>();
  const cid = consignorId || '';
  return (
    <PreviewFrame cid={cid}>
      <ConsignorDashboard consignorIdOverride={cid} basePath={`/admin/consignors/${cid}`} />
    </PreviewFrame>
  );
};

export const ConsignorPreviewContract: React.FC = () => {
  const { consignorId } = useParams<{ consignorId: string }>();
  const cid = consignorId || '';
  return (
    <PreviewFrame cid={cid}>
      <ConsignorContractView consignorIdOverride={cid} basePath={`/admin/consignors/${cid}`} />
    </PreviewFrame>
  );
};

export const ConsignorPreviewCO: React.FC = () => {
  const { consignorId } = useParams<{ consignorId: string }>();
  const cid = consignorId || '';
  return (
    <PreviewFrame cid={cid}>
      <ConsignorCOView consignorIdOverride={cid} basePath={`/admin/consignors/${cid}`} />
    </PreviewFrame>
  );
};

export const ConsignorPreviewSettlement: React.FC = () => {
  const { consignorId } = useParams<{ consignorId: string }>();
  const cid = consignorId || '';
  return (
    <PreviewFrame cid={cid}>
      <ConsignorSettlementView consignorIdOverride={cid} basePath={`/admin/consignors/${cid}`} />
    </PreviewFrame>
  );
};
